// AGC: LoteDetail - Agronomic and financial overview for a lot
// AGC: ⚠️ [DEPRECATED] This component DUPLICATES LotPage.tsx functionality
// AGC: ⚠️ [DEPRECATED] Uses mockPricingService for financial data (mock)
// AGC: ⚠️ [DEPRECATED] Use LotPage for production - it has WeatherHealth, AuditLog, etc.
// AGC: ⚠️ [DEPRECATED] Only visible in dev mode (DEV_FLAGS.ENABLE_LEGACY_SCREENS)
import React, { useMemo, useEffect } from 'react'
import { Calendar, TrendingUp, DollarSign, CheckCircle, Clock, Plus, Satellite, Lock, ChevronRight, Image as ImageIcon, AlertTriangle } from 'lucide-react'
import { MapContainer, TileLayer, Polygon } from 'react-leaflet'
import { GlassCard } from '../shared'
import { useLotes } from '../../context'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import { CROP_TYPES, getCropDisplayInfo, type Plot, type Screen } from '../../types'
import { formatArea, formatPerimeter } from '../../services/polygonService'
import { calculatePlotFinancials, formatCurrency, formatYield, getCropPrice } from '../../services/mockPricingService'
import { getLatestPlotImage } from './PlotImagesPage'
import { DEV_FLAGS } from '../../services/lotServices'
import 'leaflet/dist/leaflet.css'

interface LoteDetailProps {
  plotId: string
  onBack: () => void
  onNavigate: (screen: Screen, data?: any) => void
}

/**
 * AGC: LoteDetail - Shows complete agronomic and financial data for a lot
 * AGC: ⚠️ DEPRECATED - This component duplicates LotPage.tsx functionality
 * AGC: ⚠️ DEPRECATED - Uses mock pricing service
 * AGC: Use LotPage instead - it has WeatherHealth, AuditLog, Monitoring, etc.
 */
export const LoteDetail: React.FC<LoteDetailProps> = ({ plotId, onBack, onNavigate }) => {
  // AGC: [SOURCE_OF_TRUTH] Redirect to LotPage in production mode
  useEffect(() => {
    if (!DEV_FLAGS.ENABLE_LEGACY_SCREENS) {
      console.warn('⚠️ AGC: [DEPRECATED] LoteDetail is deprecated. Redirecting to lot-page...')
      onNavigate('lot-page', { plotId })
    }
  }, [onNavigate, plotId])

  // AGC: Show deprecation warning in dev mode
  if (DEV_FLAGS.ENABLE_LEGACY_SCREENS) {
    console.warn('⚠️ AGC: [DEPRECATED] LoteDetail uses mockPricingService. Use LotPage instead.')
  }
  const { t } = useTranslation()
  const { getPlotById } = useLotes()
  const { canUseFeature } = useSubscription()
  
  // AGC: Check if user has satellite imagery access (PRO/Premium feature)
  const hasSatelliteAccess = useMemo(() => {
    return canUseFeature('hasSatelliteImagery')
  }, [canUseFeature])
  
  // AGC: Check access to premium providers
  const hasPlanetScope = useMemo(() => {
    return canUseFeature('hasPlanetScope')
  }, [canUseFeature])
  
  const hasAirbusPleiades = useMemo(() => {
    return canUseFeature('hasAirbusPleiades')
  }, [canUseFeature])
  
  // AGC: Get latest cached image for this plot
  const latestImage = useMemo(() => {
    return getLatestPlotImage(plotId)
  }, [plotId])
  
  // AGC: Find the plot from context
  const plot = useMemo(() => {
    return getPlotById(plotId)
  }, [plotId, getPlotById])

  // AGC: Calculate financial data
  const financial = useMemo(() => {
    if (!plot) return null
    return calculatePlotFinancials(plot.cropType, plot.area, plot.managementProgress)
  }, [plot])

  if (!plot || !financial) {
    return (
      <div className="w-full bg-[#F4F5F7] dark:bg-slate-900 flex items-center justify-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Lote não encontrado</p>
      </div>
    )
  }

  // AGC: Get crop display info (handles custom/learned crop types)
  const cropDisplay = getCropDisplayInfo(plot.cropType)

  // AGC: Mock task summary (will be replaced by real tasks later)
  const totalTasks = 7
  const completedTasks = Math.floor((plot.managementProgress / 100) * totalTasks)
  const pendingTasks = totalTasks - completedTasks

  // AGC: Management status labels
  const statusLabels: Record<string, string> = {
    'por-semear': 'Por semear',
    'semeado': 'Semeado',
    'em-desenvolvimento': 'Em desenvolvimento',
    'pronto-colher': 'Pronto para colher',
  }

  return (
    <div className="w-full bg-[#F4F5F7] dark:bg-slate-900">
      {/* AGC: Simple Header - NOT sticky, TopNav from MainMenu handles navigation */}
      <div className="bg-[#0B5F4A] dark:bg-emerald-800 px-4 py-4">
        <h1 className="text-white text-xl font-bold">{plot.name}</h1>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-emerald-200">{cropDisplay.icon}</span>
          <span className="text-emerald-200">{cropDisplay.name}</span>
          <span className="text-emerald-200 text-sm">• Criado em: {new Date(plot.createdAt).toLocaleDateString('pt-BR')}</span>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4 pb-24">
        {/* AGC: General Information Section */}
        <GlassCard>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informações Gerais</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Área</p>
              <p className="text-lg font-semibold text-gray-900">{formatArea(plot.area)}</p>
            </div>
            
            <div>
              <p className="text-sm text-gray-500">Cultivo</p>
              <div className="flex items-center gap-1">
                <span>{cropDisplay.icon}</span>
                <p className="text-lg font-semibold text-gray-900">{cropDisplay.name}</p>
              </div>
            </div>

            {plot.perimeter && (
              <div>
                <p className="text-sm text-gray-500">Perímetro</p>
                <p className="text-lg font-semibold text-gray-900">{formatPerimeter(plot.perimeter)}</p>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-500">Estado</p>
              <p className="text-sm font-medium text-gray-900">{statusLabels[plot.managementStatus]}</p>
            </div>
          </div>

          {/* AGC: Management Progress */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm text-gray-600">Evolução do manejo</p>
              <p className="text-sm font-semibold text-[#0B5F4A]">{plot.managementProgress}%</p>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0B5F4A] rounded-full transition-all"
                style={{ width: `${plot.managementProgress}%` }}
              />
            </div>
          </div>
        </GlassCard>

        {/* AGC: Financial Overview Section */}
        <GlassCard>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Resumo Financeiro
          </h2>

          {/* AGC: Price and Yield Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-blue-700">Cotação atual ({cropDisplay.name}):</span>
              <span className="font-semibold text-blue-900">{getCropPrice(plot.cropType)}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-blue-700">Rendimento projetado:</span>
              <span className="font-semibold text-blue-900">{formatYield(financial.projectedYield, financial.priceUnit)}</span>
            </div>
          </div>

          {/* AGC: Revenue and Costs */}
          <div className="space-y-3">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <div>
                <p className="text-sm text-gray-500">Receita projetada</p>
                <p className="text-xs text-gray-400">({formatYield(financial.projectedYield, financial.priceUnit)} × {formatArea(plot.area)})</p>
              </div>
              <p className="text-lg font-bold text-emerald-600">{formatCurrency(financial.projectedRevenue)}</p>
            </div>

            <div className="flex justify-between items-center pb-3 border-b border-gray-200">
              <p className="text-sm text-gray-500">Custo projetado</p>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(financial.projectedCost)}</p>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm text-gray-500">Custo realizado</p>
                <div className="text-right">
                  <p className="text-lg font-semibold text-orange-600">{formatCurrency(financial.executedCost)}</p>
                  <p className="text-xs text-gray-400">{financial.executedCostPercentage.toFixed(0)}% do total</p>
                </div>
              </div>
              
              {/* AGC: Executed cost progress bar */}
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 rounded-full"
                  style={{ width: `${financial.executedCostPercentage}%` }}
                />
              </div>
            </div>

            {/* AGC: Projected Profit */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-emerald-700">Lucro projetado</p>
                <p className="text-lg font-bold text-emerald-700">
                  {formatCurrency(financial.projectedRevenue - financial.projectedCost)}
                </p>
              </div>
            </div>
          </div>
        </GlassCard>

        {/* AGC: Task Summary Section */}
        <GlassCard>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Resumo de Tarefas</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Executadas</p>
                <p className="text-lg font-semibold text-gray-900">{completedTasks} de {totalTasks}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('tasks.statusPending')}</p>
                <p className="text-lg font-semibold text-gray-900">{pendingTasks} de {totalTasks}</p>
              </div>
            </div>
          </div>

          {/* AGC: Button to add management tasks */}
          <button
            onClick={() => onNavigate('select-crop', { plotId: plot.id })}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#0B5F4A] rounded-lg text-white font-medium hover:bg-[#094935] transition-colors"
          >
            <Plus className="w-5 h-5" />
            {t('tasks.addManagementTasks')}
          </button>
        </GlassCard>

        {/* AGC: Map Preview Section (Read-only) */}
        <GlassCard padding="none">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('fields.images.mapPreview')}</h2>
          </div>
          
          <div className="h-64">
            {plot.coordinates && plot.coordinates.length >= 3 && (
              <MapContainer
                center={plot.coordinates[0]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
                dragging={true}
                scrollWheelZoom={true}
              >
                {/* AGC: Satellite/Terrain view using ESRI World Imagery */}
                <TileLayer
                  attribution='Tiles &copy; Esri'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />

                {/* AGC: Polygon visualization */}
                <Polygon
                  positions={plot.coordinates}
                  pathOptions={{
                    color: '#F59E0B',
                    fillColor: '#FCD34D',
                    fillOpacity: 0.3,
                    weight: 3,
                  }}
                />
              </MapContainer>
            )}
          </div>
        </GlassCard>

        {/* AGC: Imagens Section (PRO/Premium Feature) */}
        <GlassCard>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Satellite className="w-5 h-5 text-purple-600" />
              {t('fields.images.panelTitle')}
            </h2>
            {/* AGC: Provider availability badges */}
            <div className="flex gap-1">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                hasSatelliteAccess 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                10m
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                hasPlanetScope 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                3m
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                hasAirbusPleiades 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                50cm
              </span>
            </div>
          </div>

          {/* AGC: Free user - show upgrade prompt */}
          {!hasSatelliteAccess ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium mb-2">{t('fields.images.proFeature')}</p>
              <p className="text-gray-500 text-sm mb-4">{t('fields.images.proDescription')}</p>
              <button
                onClick={() => onNavigate('subscription')}
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all"
              >
                {t('fields.images.upgradeCta')}
              </button>
            </div>
          ) : (
            /* AGC: PRO/Premium user - show latest image or prompt to generate */
            <div>
              {latestImage ? (
                /* AGC: Show latest image preview */
                <div 
                  className="relative group cursor-pointer rounded-lg overflow-hidden"
                  onClick={() => onNavigate('lot-images', { plotId: plot.id })}
                >
                  <img
                    src={latestImage.thumbnailUrl || latestImage.imageUrl}
                    alt={t('fields.images.latestImage')}
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium">{latestImage.type.toUpperCase()}</p>
                        {/* AGC: Show provider resolution if available */}
                        {latestImage.metadata?.resolution && (
                          <span className="px-1.5 py-0.5 bg-white/20 rounded text-[10px] text-white">
                            {latestImage.metadata.resolution}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-300 text-xs">
                        {new Date(latestImage.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white" />
                  </div>
                  {/* AGC: Hover overlay */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white font-medium">{t('fields.images.viewAll')}</span>
                  </div>
                </div>
              ) : (
                /* AGC: No images yet - prompt to generate */
                <div 
                  className="text-center py-6 cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                  onClick={() => onNavigate('lot-images', { plotId: plot.id })}
                >
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <p className="text-gray-600 font-medium mb-2">{t('fields.images.noImagesYet')}</p>
                  <p className="text-gray-500 text-sm mb-4">{t('fields.images.generatePrompt')}</p>
                  <button className="px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 mx-auto">
                    <Satellite className="w-4 h-4" />
                    {t('fields.images.generateFirst')}
                  </button>
                </div>
              )}

              {/* AGC: View all images button (if has images) */}
              {latestImage && (
                <button
                  onClick={() => onNavigate('lot-images', { plotId: plot.id })}
                  className="w-full mt-3 px-4 py-2 border border-purple-200 text-purple-600 font-medium rounded-lg hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ImageIcon className="w-4 h-4" />
                  {t('fields.images.viewAllImages')}
                </button>
              )}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  )
}

export default LoteDetail

